import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux';
import { getTheme } from '@/store/slices/uiSlice.js';
import { ConfigProvider, theme as antdThemeAlg } from 'antd';
import { THEMES } from '@/constants';

// Ant Design theme configuration
const antdTheme = {
    token: {
        colorPrimary: '#ff6b35', // Orange for primary actions and switches
        colorSuccess: '#52c41a', // Green for success states
        colorWarning: '#faad14',
        colorError: '#ff4d4f',
        colorInfo: '#1890ff',
        borderRadius: 12, // Rounded corners (Lunar style)
        wireframe: false,
    },
    components: {
        Layout: {
            headerBg: '#ffffff',
            siderBg: '#ffffff',
            bodyBg: '#f5f5f5',
        },
        Menu: {
            itemBg: 'transparent',
            itemSelectedBg: 'rgba(24, 144, 255, 0.1)',
            itemHoverBg: 'rgba(24, 144, 255, 0.05)',
        },
        Card: {
            headerBg: '#fafafa',
        },
        Table: {
            headerBg: '#fafafa',
            rowHoverBg: 'rgba(0, 0, 0, 0.02)', // Light grey hover (Lunar style)
        },
        Switch: {
            colorPrimary: '#ff6b35', // Orange for checked state
            colorPrimaryHover: '#ff8555',
        },
    },
}

// Dark theme configuration
const antdDarkTheme = {
    ...antdTheme,
    token: {
        ...antdTheme.token,
        colorBgBase: '#141414',
        colorBgContainer: '#1f1f1f',
        colorBgElevated: '#262626',
        colorBorder: '#434343',
        colorText: '#ffffff',
        colorTextSecondary: '#a6a6a6',
    },
    components: {
        ...antdTheme.components,
        Layout: {
            headerBg: '#1f1f1f',
            siderBg: '#1f1f1f',
            bodyBg: '#141414',
        },
        Menu: {
            itemBg: 'transparent',
            itemSelectedBg: 'rgba(24, 144, 255, 0.2)',
            itemHoverBg: 'rgba(24, 144, 255, 0.1)',
        },
        Card: {
            headerBg: '#262626',
        },
        Table: {
            headerBg: '#262626',
            rowHoverBg: 'rgba(255, 255, 255, 0.05)', // Light hover in dark mode
        },
        Switch: {
            colorPrimary: '#ff6b35', // Orange for checked state
            colorPrimaryHover: '#ff8555',
        },
    },
}

export const ThemeProvider = ({ children }) => {
    const savedTheme = useSelector(getTheme); // 'dark' | 'light'

    // Option: bám theo system khi bạn muốn fallback (nếu Redux chưa set)
    const prefersDark = useMemo(
        () => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches,
        []
    );

    // Chọn dark hay không: Redux là số 1; nếu Redux không có thì lấy system
    const isDark = savedTheme
        ? savedTheme === THEMES.DARK
        : prefersDark;

    // Gắn / gỡ class 'dark' và data-theme trên <html>
    useEffect(() => {
        const root = document.documentElement;
        if (isDark) {
            root.classList.add('dark');
            root.setAttribute('data-theme', 'dark');
        } else {
            root.classList.remove('dark');
            root.setAttribute('data-theme', 'light');
        }
        
        // Cleanup function để đảm bảo theme được set đúng khi component unmount
        return () => {
            // Không cần cleanup vì theme sẽ được set lại khi component mount lại
        };
    }, [isDark]);

    // (Khuyến nghị) cho Ant Design dùng algorithm để được dark mode “đúng chuẩn”:
    const antdResolvedTheme = useMemo(() => {
        const base = isDark ? antdDarkTheme : antdTheme;
        return {
            ...base,
            algorithm: isDark ? antdThemeAlg.darkAlgorithm : antdThemeAlg.defaultAlgorithm,
        };
    }, [isDark]);

    return <ConfigProvider theme={antdResolvedTheme}>{children}</ConfigProvider>;
};
